import type { FirebaseApp } from 'firebase/app';
import {
  getAuth,
  onAuthStateChanged,
  signInWithCustomToken,
  signOut,
} from 'firebase/auth';

/**
 * Resolves once the SDK has finished restoring (or failing to restore) its
 * persisted session. On a reload the Firebase session comes back from IndexedDB
 * on its own, but only after this first callback — reading `currentUser`
 * synchronously would report `null` and strand a still-valid operator at the
 * sign-in screen.
 */
export function waitForRestoredFirebaseUser(
  firebaseApp: FirebaseApp,
): Promise<boolean> {
  return new Promise((resolve) => {
    const unsubscribe = onAuthStateChanged(
      getAuth(firebaseApp),
      (user) => {
        unsubscribe();
        resolve(Boolean(user));
      },
      () => {
        unsubscribe();
        resolve(false);
      },
    );
  });
}

/**
 * Establishes the browser's Firebase session from the backend-minted custom
 * token. The FireCMS RTDB and Storage delegates resolve `getDatabase(app)` /
 * `getStorage(app)` per call, so they pick this up automatically — provided it
 * is the SAME FirebaseApp instance they were given.
 */
export async function signInAdmin(
  firebaseApp: FirebaseApp,
  customToken: string,
): Promise<void> {
  await signInWithCustomToken(getAuth(firebaseApp), customToken);
}

/**
 * The Firebase JS SDK persists its session in IndexedDB, not in a key the Admin
 * controls, so clearing localStorage alone would leave a signed-out operator
 * with live read access. This must run on every sign-out and expiry path.
 */
export async function signOutAdmin(firebaseApp: FirebaseApp): Promise<void> {
  await signOut(getAuth(firebaseApp));
}
